<script setup lang="ts">
import { computed, ref } from "vue";
import { useAuthStore } from "@/store/auth";
import { useClientAuthStore } from "@/app/stores/clientAuth";
import type { DirectMessageParticipants } from "../directMessageIdentity";
import { useRoute, useRouter } from "vue-router";
import { UiButton, UiText } from "@shopexperts/shared";
import { getConvexAuthInfo, projectFns, runProjectsConvexMutation } from "../convexClient";
import { useChatQuery } from "../composables/useChatQuery";
import { useThreadMessages } from "../composables/useThreadMessages";
import { useChatActions } from "../composables/useChatActions";
import { captureConvexError } from "../telemetry";
import DirectConversationHeader from "../components/DirectConversationHeader.vue";
import DirectMessagesInbox from "../components/DirectMessagesInbox.vue";
import { useDirectChatPeers } from "../composables/useDirectChatPeers";
import DirectProjectDialog from "../components/DirectProjectDialog.vue";
import ChatFeed from "../components/ChatFeed.vue";
import ChatComposer from "../components/ChatComposer.vue";

import { readMessageDraft, saveMessageDraft, removeMessageDraft } from "../directMessageDraft";

const authStore = useAuthStore();
const account = useClientAuthStore();
const route = useRoute();
const router = useRouter();
const side = computed(() => route.path.startsWith("/expert") ? "expert" as const : "client" as const);
const selectedId = computed(() => typeof route.params.threadId === "string" ? route.params.threadId : null);
const room = useChatQuery(projectFns.getDirectRoom, computed(() => selectedId.value ? { thread_id: selectedId.value as never } : null));
const threadId = computed(() => room.data.value?.thread_id ?? null);
const auth = computed(() => room.data.value ? getConvexAuthInfo() : null);
const chat = useThreadMessages(threadId);
const actions = useChatActions(computed(() => undefined), side, threadId);
const focusMessageId = computed(() => typeof route.query.messageId === "string" ? route.query.messageId : null);
const draftId = computed(() => typeof route.query.draft === "string" ? route.query.draft : '');
const draft = computed(() => {
  const value = readMessageDraft(draftId.value);
  return value?.threadId === selectedId.value && value?.userId === authStore.user?.id ? value : null;
});
const draftWarning = ref('');
function updateDraft(html: string) {
  if (!draft.value) return;
  try { saveMessageDraft(draftId.value, { ...draft.value, html }); }
  catch { draftWarning.value = 'Your changes could not be saved on this device. Keep this page open until you send.'; }
}
function clearDraft() {
  if (!draftId.value) return;
  try { removeMessageDraft(draftId.value); } catch { /* The URL is cleared even when browser storage is unavailable. */ }
  router.replace({ query: { ...route.query, draft: undefined } });
}
const readError = ref(false);
let lastReadReport: string | null = null;
const projectDialogOpen = ref(false);
const profiles = useDirectChatPeers(computed(() => threadId.value ? [threadId.value] : []));
const peer = computed(() => threadId.value ? profiles.peer(threadId.value) : undefined);

const directParticipants = computed<DirectMessageParticipants>(() => ({
  users: profiles.users.value,
  self: auth.value ? {
    user_id: auth.value.userId,
    name: account.fullName || [authStore.user?.first_name, authStore.user?.last_name].filter(Boolean).join(" ") || "You",
    avatar_url: account.profilePicture ?? authStore.user?.photo ?? null,
  } : undefined,
}));

async function markRead(messageId: string) {
  if (!threadId.value || !room.data.value?.is_unread || lastReadReport === messageId) return;
  lastReadReport = messageId;
  try {
    await runProjectsConvexMutation(projectFns.markThreadRead, {
      thread_id: threadId.value, through_message_id: messageId as never,
    }, { operation: "chat.mark_read" });
    readError.value = false;
  } catch (error) {
    lastReadReport = null;
    readError.value = true;
    captureConvexError(error, { operation: "chat.mark_read_failed", threadId: threadId.value, level: "warning" });
  }
}
</script>

<template>
  <main class="flex min-h-0 flex-1 flex-col bg-secondary p-3 md:px-8 md:pb-6 md:pt-4">
    <div class="relative flex min-h-0 w-full flex-1 flex-col gap-3 md:gap-4">
    <DirectMessagesInbox v-if="!selectedId" :side="side" />
    <section v-else class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-se-gray-200 bg-white shadow-sm" aria-label="Conversation">
      <DirectConversationHeader :side="side" :peer="peer" :name="profiles.name(selectedId)"
        @back="router.push(`/${side}/messages`)" @create-project="projectDialogOpen = true" />
      <div v-if="profiles.error.value" class="flex items-center justify-between gap-3 border-b border-se-gray-200 px-6 py-3" role="alert">
        <UiText as="p" variant="small" text="Participant profile could not be loaded." />
        <UiButton variant="ghost" label="Retry" @click="profiles.refetch()" />
      </div>
      <UiText v-if="room.isLoading.value" as="p" variant="p-small" text="Opening conversation…" class="p-6" />
      <div v-else-if="room.error.value" role="alert" class="p-6">
        <UiText as="p" variant="p" text="This conversation could not be opened." />
        <UiButton variant="outline" label="Try again" @click="room.refresh" />
      </div>
      <template v-else-if="room.data.value && threadId && auth">
        <ChatFeed :key="threadId" :messages="chat.messages.value" :participants-display="null" :direct-participants="directParticipants"
          :current-user-id="auth.userId" :current-org-id="auth.orgId" :side="side"
          :is-ready="chat.isReady.value" :is-loading-more="chat.isLoadingMore.value" :has-more="chat.hasMore.value"
          :focus-message-id="focusMessageId" :empty-text="peer ? `Say hello to ${peer.name} to get the conversation started.` : undefined"
          @load-more="chat.loadMore" @read-through="markRead" />
        <div v-if="chat.loadError.value" class="p-3" role="alert">
          <UiText as="p" variant="p-small" :text="chat.loadError.value" />
          <UiButton variant="outline" size="sm" label="Retry messages" @click="chat.refresh" />
        </div>
        <UiText v-if="readError" as="p" class="px-4" variant="small" text="Read status could not be updated." />
        <div v-if="draftId" role="status"><UiText as="p" variant="p-small" class="px-4 py-2"
          :text="draftWarning || (draft ? 'Your message is ready. Review it below, then send when you’re ready.' : 'Your saved message is no longer available in this tab. Please write your message below.')" /></div>
        <div class="shrink-0">
          <ChatComposer :key="`${threadId}:${draftId}`" :side="side" :acting-org-id="auth.orgId" :thread-id="threadId"
            :compact="true" :initial-message="draft?.html" @draft-change="updateDraft" @sent="clearDraft" :send-message="actions.sendMessage.mutateAsync" :send-note="actions.sendNote.mutateAsync"
            :upload-attachments="actions.uploadAttachments.mutateAsync" />
        </div>
        <DirectProjectDialog :key="threadId" v-model="projectDialogOpen" :thread-id="threadId" :side="side" :peer-name="peer?.name ?? 'Conversation participant'" />
      </template>
    </section>
    </div>
  </main>
</template>
